import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, DropdownMenu, MaterialSymbol, Pagination, SubAccountType, formattedCurrency, withStyles } from 'vopay';
import { useToast } from '#shared/providers/Toast';
import { useAlertDialog } from '#shared/providers/AlertDialog';
import { useBlockingLoader } from '#shared/providers/Loader';
import { Button, Skeleton, TableFiltersContainer, TransactionStatus, Table as TableUI } from '#shared/components/UI';
import useDeactivateSubAccount from '#shared/hooks/useApi/useDeactivateSubAccount';
import EditSubAccountDrawer, { EditableSubAccount } from './EditSubAccount/EditSubAccountDrawer';
import AccountPermissionsDrawer from './Permissions/AccountPermissionsDrawer';
import useSubAccountList from './useSubAccountList';
import SubAccountSearchForm from './SubAccountSearchForm';
import Table, { ColConfig } from './Table/Table';
import {
  buildFlatWithTree,
  buildNested,
  filterActiveAccounts,
  mapFlatSubAccountWithTree,
  mapNestedSubAccounts,
} from './subAccountRows';
import { SubAccountsListsShow } from './types';
import styles from './SubAccountList.module.scss';

const cn = withStyles(styles);

// Sub-account types eligible for the Edit action (legal name / email).
const EDITABLE_SUB_ACCOUNT_TYPES: SubAccountType[] = ['technology', 'division', 'ledger'];

// The filters + tree table + paginator + per-row actions that used to live inside client-dashboard's
// SubAccountsPage Card — mounted by the host as <SubAccountList.Table /> under the feature root, which owns
// the fetch, the filters and the block / unblock flows this reads from context. The host keeps the page
// header, the org-chart view and its toggle, the drawers behind hostActions, and any container around this.
export type SubAccountListTableProps = {
  /**
   * The padding the client portal's page has always had between the filters and the table. A host
   * whose own chrome sets that spacing (the partner account page's tab, ID-13430) turns it off.
   */
  hasFiltersPadding?: boolean;
};

export default function SubAccountListTable({ hasFiltersPadding = true }: SubAccountListTableProps) {
  const {
    account,
    userPermissions,
    hostPaths,
    hostActions: { onCreateAccountTransfer, onOpenUserManagement, onOpenApprovalWorkflow },
    filters: { status, currency, country, searchTerm },
    search,
    pagination: { pageCount, pageIndex, setPageIndex },
    subAccounts,
    mappedSubAccounts,
    isLoadingSubAccounts,
    allAccountsBlocked,
    refetchSubAccounts,
    blockAccount,
    unblockAccount,
  } = useSubAccountList();

  const { deactivateSubaccount } = useDeactivateSubAccount();
  const toast = useToast();
  const alertDialog = useAlertDialog();
  const blockingLoader = useBlockingLoader();
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList' });
  const { t: tButtons } = useTranslation('translations', { keyPrefix: 'Shared.Buttons' });

  const [showTransactionFilter, setShowTransactionFilter] = useState(true);
  const [isEditDrawerOpen, setIsEditDrawerOpen] = useState(false);
  const [subAccountToEdit, setSubAccountToEdit] = useState<EditableSubAccount>();
  const [isPermissionsDrawerOpen, setIsPermissionsDrawerOpen] = useState(false);
  const [selectedAccountID, setSelectedAccountID] = useState<string>();
  const [selectedAccountName, setSelectedAccountName] = useState<string>();
  const [selectedAccountCurrency, setSelectedAccountCurrency] = useState<string>();

  const hasSearch = useMemo(
    () => searchTerm !== '' || status === 'NotActive' || currency !== '' || country !== '',
    [searchTerm, status, currency, country],
  );

  const showNestedView = useMemo(
    () => !hasSearch || (searchTerm === '' && currency === '' && country === '' && status === 'Active'),
    [hasSearch, searchTerm, currency, country, status],
  );

  async function handleDeactivationDialogue(accountID: string) {
    const result = await alertDialog.caution(
      t('DeactivateDialog.title', { defaultValue: 'Deactivate Sub-Account?' }),
      <div className={cn('dialogContent')}>
        {t('DeactivateDialog.content', { defaultValue: 'Are you sure you want to deactivate this sub-account?' })}
      </div>,
      {
        abortLabel: tButtons('cancel', { defaultValue: 'Cancel' }),
        confirmLabel: t('Buttons.deactivate', { defaultValue: 'Deactivate' }),
      },
    );

    if (result !== 'confirm') return;

    try {
      blockingLoader.show();
      await deactivateSubaccount({ SubaccountID: accountID });
      await refetchSubAccounts();
    } catch (error) {
      // The hook already logged; surface the server's message to the user, as the old page did.
      if (error instanceof Error) toast.neutral(error.message);
    } finally {
      blockingLoader.hide();
    }
  }

  const nestedSubAccounts = useMemo(() => buildNested(subAccounts).nested, [subAccounts]);

  const flatSubAccountFiltered = useMemo(() => {
    const flatSubAccountsWithTree = buildFlatWithTree(mappedSubAccounts);

    return country ? flatSubAccountsWithTree.filter((data) => data.Country === country) : flatSubAccountsWithTree;
  }, [mappedSubAccounts, country]);

  // Filter nested accounts for table view only (when status is Active)
  const filteredNestedForTable = useMemo(() => {
    return status === 'Active' ? filterActiveAccounts(nestedSubAccounts) : nestedSubAccounts;
  }, [nestedSubAccounts, status]);

  // Status through actions are shared by both views; the two copies the old page carried differed only in
  // the blocked-balance colour, the available-balance divider and a few flat-view dropdown-item styles.
  function buildSharedColumns(view: 'nested' | 'flat'): ColConfig<SubAccountsListsShow, string>[] {
    const isFlat = view === 'flat';
    const blockedBalanceColor = isFlat ? '#2D2D2DB3' : '#2D2D2D0D';
    const flatItemStyle = isFlat ? { gap: '21px' } : undefined;

    return [
      {
        id: 'status',
        header: t('Attributes.status', { defaultValue: 'Status' }),
        align: 'left',
        template: ({ data }) => (
          <TransactionStatus
            status={data.AccountStatus === 'Blocked' ? 'blocked' : data.IsActive ? 'active' : 'not active'}
            label={
              data.AccountStatus === 'Blocked'
                ? t('Attributes.blockedBadge', { defaultValue: 'Blocked' })
                : data.IsActive
                  ? t('Attributes.activeBadge', { defaultValue: 'Active' })
                  : t('Attributes.notActiveBadge', { defaultValue: 'Not Active' })
            }
          />
        ),
        style: {
          whiteSpace: 'nowrap',
        },
      },
      {
        id: 'country',
        header: t('Attributes.country', { defaultValue: 'Country' }),
        field: 'Country',
        align: 'left',
      },
      {
        id: 'currency',
        header: t('Attributes.currency', { defaultValue: 'Currency' }),
        field: 'Currency',
        align: 'left',
      },
      {
        id: 'pendingBalance',
        header: t('Attributes.pendingBalance', { defaultValue: 'Pending Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? blockedBalanceColor : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.PendingBalance)}`}
          </span>
        ),
      },
      {
        id: 'availableBalance',
        header: t('Attributes.availableBalance', { defaultValue: 'Available Balance' }),
        align: 'left',
        template: ({ data }) => (
          <span style={{ color: allAccountsBlocked ? blockedBalanceColor : 'inherit' }}>
            {`${data.Currency} $${formattedCurrency(data.AvailableBalance)}`}
          </span>
        ),
        hasDivider: view === 'flat',
      },
      {
        id: 'view',
        header: '',
        align: 'right',
        template: ({ data }) => (
          <Box gap="sm" jc="fe" ai="center">
            {/* The host actions are offered only where the host has a surface for them; a host without
                one (the partner account page) leaves the handler out and the row never shows it. */}
            {data.IsActive && userPermissions.canCreateAccountTransfer && onCreateAccountTransfer && (
              <Button
                id={`view-sub-account-${data.AccountID.replace(/\s+/g, '')}`}
                size="smallest"
                colorScheme="secondary"
                onClick={() =>
                  onCreateAccountTransfer({
                    accountID: data.AccountID,
                    currency: data.Currency,
                    token: data.Token,
                  })
                }
              >
                {t('Table.Row.transferButton', { defaultValue: 'Transfer' })}
              </Button>
            )}

            <DropdownMenu>
              <DropdownMenu.Trigger asChild>
                <Button size="smallest" colorScheme="secondary" iconRight="keyboard_arrow_down">
                  {t('Table.Row.actionsButton', { defaultValue: 'Actions' })}
                </Button>
              </DropdownMenu.Trigger>

              <DropdownMenu.Content align="end">
                {userPermissions.canAddSubaccounts && EDITABLE_SUB_ACCOUNT_TYPES.includes(data.SubAccountType) && (
                  <DropdownMenu.Item
                    onSelect={() => {
                      setSubAccountToEdit({
                        AccountID: data.AccountID,
                        AccountName: data.AccountName,
                        LegalBusinessName: data.LegalBusinessName,
                        Email: data.Email,
                      });

                      setIsEditDrawerOpen(true);
                    }}
                    left={<MaterialSymbol icon="edit" size={18} />}
                  >
                    {t('Table.Row.editButton', { defaultValue: 'Edit' })}
                  </DropdownMenu.Item>
                )}

                <DropdownMenu.Item
                  onSelect={() => {
                    setSelectedAccountID(data.AccountID);
                    setSelectedAccountName(data.AccountName);
                    setSelectedAccountCurrency(data.Currency);
                    setIsPermissionsDrawerOpen(true);
                  }}
                  left={<MaterialSymbol icon="settings" fillAmount={isFlat ? 1 : undefined} size={18} />}
                  style={flatItemStyle}
                >
                  {t('Table.Row.permissionsButton', { defaultValue: 'Permissions' })}
                </DropdownMenu.Item>

                {onOpenUserManagement && (
                  <DropdownMenu.Item
                    onSelect={() =>
                      onOpenUserManagement({
                        accountName: data.AccountName,
                        token: data.Token,
                      })
                    }
                    left={<MaterialSymbol icon="manage_accounts" size={18} />}
                  >
                    {t('Table.Row.userManagementButton', { defaultValue: 'User Management' })}
                  </DropdownMenu.Item>
                )}

                {data.IsActive && onOpenApprovalWorkflow && (
                  <DropdownMenu.Item
                    onSelect={() =>
                      onOpenApprovalWorkflow({
                        accountID: data.AccountID,
                        accountName: data.AccountName,
                        token: data.Token,
                      })
                    }
                    left={<MaterialSymbol icon="task_alt" size={18} />}
                  >
                    {t('Table.Row.approvalWorkflowButton', { defaultValue: 'Approval Workflow' })}
                  </DropdownMenu.Item>
                )}

                {account.accountType === 'Secured Account' &&
                  userPermissions.canManageBlockedAccounts &&
                  (data.AccountStatus === 'Blocked' ? (
                    <DropdownMenu.Item
                      onSelect={() => unblockAccount(data.AccountID, data.AccountName)}
                      left={<MaterialSymbol icon="lock_open" size={18} />}
                      style={flatItemStyle}
                    >
                      {t('Table.Row.unblockButton', { defaultValue: 'Unblock Sub-Account' })}
                    </DropdownMenu.Item>
                  ) : (
                    <DropdownMenu.Item
                      onSelect={() => blockAccount(data.AccountID, data.AccountName)}
                      colorScheme="destructive"
                      left={<MaterialSymbol icon="block" size={18} />}
                      style={flatItemStyle}
                    >
                      {t('Table.Row.blockButton', { defaultValue: 'Block Sub-Account' })}
                    </DropdownMenu.Item>
                  ))}

                {userPermissions.canDeactivateSubAccounts &&
                  (data.SubAccountType === 'division' || data.SubAccountType === 'ledger') &&
                  data.IsActive && (
                    <DropdownMenu.Item
                      onSelect={() => handleDeactivationDialogue(data.AccountID)}
                      colorScheme="destructive"
                      left={<MaterialSymbol icon="remove_circle_outline" size={18} />}
                    >
                      {t('Table.Row.deactivateButton', { defaultValue: 'Deactivate' })}
                    </DropdownMenu.Item>
                  )}
              </DropdownMenu.Content>
            </DropdownMenu>
          </Box>
        ),
      },
    ];
  }

  return (
    <>
      <Box flx="col" pb={hasFiltersPadding ? 'md' : undefined} base="8">
        <TableFiltersContainer
          open={showTransactionFilter}
          onOpenChange={setShowTransactionFilter}
          filtersApplied={hasSearch}
        >
          <Box flx="col" pb="sm" base="8" ai="fe">
            <TableFiltersContainer.Trigger />
          </Box>

          <TableFiltersContainer.Content>
            <SubAccountSearchForm
              status={status}
              currency={currency}
              country={country}
              searchTerm={searchTerm}
              search={(values) => search(values)}
            />
          </TableFiltersContainer.Content>
        </TableFiltersContainer>
      </Box>

      <Box className={cn('subAccountsTableContainer', 'scrollbar')}>
        {/* Keyed per view so switching views remounts Table: it keeps its rows in internal state, so a
              reused instance would hand the flat columns one render of stale nested rows (no `tree`). */}
        {isLoadingSubAccounts ? (
          <Skeleton height="320px" containerClassName={cn('tableSkeleton')} />
        ) : showNestedView ? (
          <Table
            key="nested"
            id="subAccountsList"
            hasPlaceholderArrows
            defaultExpanded
            rows={mapNestedSubAccounts(filteredNestedForTable)}
            columns={[
              {
                id: 'accountName',
                header: t('Attributes.accountName', { defaultValue: 'Account Name' }),
                align: 'left',
                template: ({ data }) => (
                  <TableUI.Link to={hostPaths.subAccountDetail(data.AccountID, data.Currency)}>
                    {data.AccountName}
                  </TableUI.Link>
                ),
              },
              ...buildSharedColumns('nested'),
            ]}
          />
        ) : (
          <Table
            key="flat"
            id="subAccountsList"
            hasPlaceholderArrows={false}
            rows={mapFlatSubAccountWithTree(flatSubAccountFiltered)}
            columns={[
              {
                id: 'accountName',
                header: t('Attributes.accountName', { defaultValue: 'Account Name' }),
                align: 'left',
                template: ({ data }) => {
                  const onClick = (value: string) => {
                    search({ status: '', currency, country: '', searchTerm: value });
                  };

                  return (
                    <Box flx="col" ai="fs">
                      <Breadcrumbs tree={data.tree} onClick={onClick} />

                      <TableUI.Link to={hostPaths.subAccountDetail(data.AccountID, data.Currency)}>
                        {data.AccountName}
                      </TableUI.Link>
                    </Box>
                  );
                },
              },
              ...buildSharedColumns('flat'),
            ]}
          />
        )}
      </Box>

      <Box flx="row" jc="center" mt="md" base="8">
        {pageCount > 1 && (
          // vopay's Pagination, not the UI barrel's Paginator: the old page's control, kept 1-1 (its DOM,
          // incl. data-testid="pagination", is what the list-page specs assert on).
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => setPageIndex(e.selected)}
          />
        )}
      </Box>

      {/* Keyed on the account so switching sub-accounts remounts the drawer with a fresh permissions
          fetch/state, the way the conditionally-mounted sibling drawers below get a fresh instance per
          target. Without it, this permanently-mounted drawer reuses one hook instance across accounts,
          and a slow fetch for a previous account could land after a newer one and overwrite it. */}
      <AccountPermissionsDrawer
        key={selectedAccountID}
        isOpen={isPermissionsDrawerOpen}
        onClose={() => setIsPermissionsDrawerOpen(false)}
        accountID={selectedAccountID}
        accountName={selectedAccountName}
        currency={selectedAccountCurrency}
      />

      {subAccountToEdit && (
        <EditSubAccountDrawer
          isOpen={isEditDrawerOpen}
          onClose={() => setIsEditDrawerOpen(false)}
          subAccount={subAccountToEdit}
          onSaved={async () => {
            try {
              await refetchSubAccounts();
            } catch (error) {
              // The hook already logged; a failed re-fetch is a non-blocking toast, as the old page's onSaved did.
              if (error instanceof Error) toast.neutral(error.message);
            }
          }}
        />
      )}
    </>
  );
}

type BreadcrumbsProps = {
  tree: string[];
  onClick: (value: string) => void;
};

function Breadcrumbs({ tree, onClick }: BreadcrumbsProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'Shared.SubAccountList' });
  const [expanded, setExpanded] = useState(false);

  if (tree.length === 0) return null;

  const renderBreadcrumbItem = (item: string, index: number) => (
    <Fragment key={item}>
      <button type="button" onClick={() => onClick(item)}>
        {item}
      </button>

      {index < tree.length - 1 && ' > '}
    </Fragment>
  );

  const renderCollapsedBreadcrumbs = () => (
    <>
      {renderBreadcrumbItem(tree[0], 0)}
      {'>'}

      <button type="button" onClick={() => setExpanded(!expanded)}>
        ...
      </button>

      {'>'}
      {renderBreadcrumbItem(tree[tree.length - 1], tree.length - 1)}
    </>
  );

  return (
    <nav aria-label={t('breadcrumbsLabel', { defaultValue: 'Breadcrumbs' })}>
      <span className={cn('breadcrumbs')}>
        {tree.length <= 2 || expanded ? tree.map(renderBreadcrumbItem) : renderCollapsedBreadcrumbs()}
      </span>
    </nav>
  );
}
